import { useState } from "react"
import { UseQueryResult, useQuery, UseQueryOptions, useMutation } from "react-query"
import { AxiosRequestConfig } from 'axios'
import jsFileDownlaod from 'js-file-download'
import moment from "moment"

import { APIEndpoints } from "../constants/api-endpoints"
import { HttpRequest, HttpBaseRequest } from "../services/_http"
import { QueryKeys } from "./_query_keys"
import { useNotificationBar } from "context/notificationContext"

type CreateEpisodeVariables = {
  data: FormData
  config?: AxiosRequestConfig
}

export type PresignFileType = 'video' | 'thumbnail' | 'transcript' | 'resource'

export type PresignSingleResponse = {
  entryId: string
  key: string
  uploadUrl: string
}

// Ask the server for a presigned S3 PUT url for a single file. `type` tells
// the server which folder to key it under (episodes/, episode/thumbnail/, etc.)
export const presignEpisodeFile = async (
  filename: string,
  contentType: string,
  type: PresignFileType
): Promise<PresignSingleResponse> => {
  const { data } = await HttpRequest({
    url: `${APIEndpoints.Episodes}/presign-upload`,
    method: 'POST',
    data: { filename, contentType, type },
  })
  return data?.results ?? data
}

// Upload a file straight to S3 via its presigned url (no auth header, no baseURL rewrite)
export const uploadFileToPresignedUrl = (
  uploadUrl: string,
  file: File,
  onUploadProgress?: (e: any) => void
) =>
  HttpBaseRequest({
    url: uploadUrl,
    method: 'PUT',
    data: file,
    headers: { 'Content-Type': file.type },
    onUploadProgress,
  })

// Tell the server every presigned file finished uploading + save episode metadata (create flow)
export const completeEpisodeUpload = async (payload: any) => {
  const { data } = await HttpRequest({
    url: `${APIEndpoints.Episodes}/complete-upload`,
    method: 'POST',
    data: payload,
  })
  return data?.results ?? data
}

// Same shape as completeEpisodeUpload, but for an existing episode (edit flow)
export const updateEpisodeUpload = async (payload: any) => {
  const { data } = await HttpRequest({
    url: `${APIEndpoints.Episodes}/update-upload`,
    method: 'POST',
    data: payload,
  })
  return data?.results ?? data
}


export const useEpisodesData = (series_id: any, searchOptions?: any, options?: any): UseQueryResult<any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.EPISODES, series_id, searchOptions]
    const keepPreviousData = false

    return useQuery({
        queryKey,
        queryFn: () => HttpRequest({
            url: `${APIEndpoints.Episodes}`,
            params: searchOptions,
            method
        }),
        refetchOnWindowFocus: false,
        keepPreviousData,
        ...options
    })
}


export const useGetEpisodData = (searchOptions?: any, options?: UseQueryOptions): UseQueryResult<any> => {

    const method = 'GET'
    const queryKey = [QueryKeys.EPISODES, searchOptions]
    /** To keeping old data until response received with new data */
    const [keepPreviousData, setkeepPreviousData] = useState(true)
    return useQuery({
        queryKey,
        queryFn: () => HttpRequest({
            url: APIEndpoints.Episodes,
            params: searchOptions,
            method
        }),
        refetchOnWindowFocus: false,
        keepPreviousData,
        onError: () => setkeepPreviousData(false),
        onSuccess: () => setkeepPreviousData(true),
        ...options
    })
}

export const useDeleteEpisode = (options?: any) => {
    const method = 'DELETE'
    return useMutation(({ id }: { id: any }) => HttpRequest({
        url: `${APIEndpoints.Episodes}/delete/${id}`,
        method,
    }), options)
}
export const useStatusEpisode = (options?: any) => {

    const method = 'PATCH'

    return useMutation(({ data, id }: { data: any, id: any }) => HttpRequest({
        url: `${APIEndpoints.Episodes}/status/${id}`,
        method,
        headers: { 'Content-Type': 'application/json' },
        data: typeof data === 'string' ? data : JSON.stringify(data)
    }), options)
}
export const useUpdateEpisodeAssetType = (options?: any) => {

    const method = 'PUT'

    return useMutation(({ data, id }: { data: any, id: any }) => HttpRequest({
        url: `${APIEndpoints.Episodes}/asset/${id}`,
        method,
        data
    }), options)
}


export const useEpisodeDetailData = (episode_id: any, mediaType: any, entry_id: any, options?: UseQueryOptions): UseQueryResult<any> => {

    const method = 'GET'
    const queryKey = [QueryKeys.SERIES_EPISODE, episode_id]

    return useQuery({
        queryKey,
        queryFn: () => HttpRequest({
            url: `${APIEndpoints.SeriesEpisode}/${episode_id}`,
            method,
            params: {
                mediaType,
                entry_id
            }
        }),
        refetchOnWindowFocus: false,
        cacheTime: 0,
        ...options
    })
}

export const useAddEpisode = (options?: any) => {

    const method = 'POST'

    return useMutation(({ data }: { data: any }) => HttpRequest({
        url: `${APIEndpoints.Episodes}`,
        method,
        data
    }), options)
}


export const useEpisodeSearch = (searchTerm = '') => {
    return useQuery(
        ['episodes-search', searchTerm],
        async () => {
            if (!searchTerm.trim()) {
                return [];
            }

            const { data } = await HttpRequest({
                url: `/episodes/search/all?search=${encodeURIComponent(searchTerm)}`,
                method: 'GET'
            });
            return data;
        },
    );
};


export const fetchEpisodesSearchApi = async (search: string) => {
   const { data } = await HttpRequest({
                url: `/episodes/search/all?search=${encodeURIComponent(search)}`,
                method: 'GET'
            });
            return data?.results;
};


export const useEpisodesSearch = (searchTerm: string) => {
  return useQuery({
    queryKey: ["episode-search", searchTerm],
    enabled: Boolean(searchTerm.trim()),
    keepPreviousData: true,
    staleTime: 30 * 1000, // 30 seconds
    queryFn: async () => {
      const { data } = await HttpRequest({
        url: `/episodes/search/all?search=${encodeURIComponent(searchTerm)}`,
        method: "GET"
      })
      return data
    }
  })
}

export const useEpisodesSearchUnlinked = (searchTerm: string) => {
  return useQuery({
    queryKey: ["episode-search", searchTerm],
    enabled: Boolean(searchTerm.trim()),
    keepPreviousData: true,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data } = await HttpRequest({
        url: `/episodes/search/unlinked?search=${encodeURIComponent(searchTerm)}`,
        method: "GET"
      })
      return data
    }
  })
}


// Specific create endpoint for standalone create form
export const useCreateEpisode = (options?: any) => {
  const method = 'POST'

  return useMutation<
    any, 
    any, 
    CreateEpisodeVariables
  >(
    ({ data, config }) =>
      HttpRequest({
        url: `${APIEndpoints.Episodes}/create`,
        method,
        data,
        ...config,
      }),
    options
  )
}

export const useUpdateEpisode = (options?: any) => {

    const method = 'PUT'

    return useMutation(({ data, episode_id, config }: { data: any, episode_id: string, config?: any }) => HttpRequest({
        url: `${APIEndpoints.Episodes}/${episode_id}`,
        method,
        data,
          ...config,
    }), options)
}


// Get episode details by id (absolute endpoint)
export const useGetEpisodeDetails = (episode_id: any, options?: UseQueryOptions): UseQueryResult<any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.EPISODE_DETAIL, episode_id]

    return useQuery({
        queryKey,
        queryFn: () => HttpRequest({
            url: `/episodes/${episode_id}`,
            method,
        }),
       enabled: !!episode_id,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        ...options
    })
}


export const useExportAllEpisodes = () => {
    const method = 'GET'
    const notification = useNotificationBar()
    return (additionalQueryParams: Object = {}) => {
        let processingIdx = notification.push({
            type: 'info',
            title: 'Processing file please wait',
            message: 'Your data will be downloaded soon ...'
        })
        HttpRequest({
            url: `${APIEndpoints.ExportAllEpisodes}`,
            method,
            params: {
                ...additionalQueryParams
            },
            responseType: 'blob'
        }).then(data => {
            jsFileDownlaod(data?.data, `episodes_${moment().format("DD-MMM-YYYY-hh:mm:ss")}.xlsx`)
            processingIdx && notification.hide(processingIdx)
            notification.push({
                type: 'success',
                title: 'Downloaded',
                message: 'Exported data has been downloaded successfully !'
            })
        })
    }
}